import { useState, useMemo } from 'react';
import type { LookCalendario } from '../../../hooks/useClienteCalendario';
import type { Prenda } from '../../../hooks/useClienteArmario';
import { useLookUpload } from '../../../hooks/useLookUpload';

interface LookUsado extends LookCalendario {
  foto_real_url?: string | null;
  calificacion?: number | null;
  comentario_cliente?: string | null;
}

interface HistorialProps {
  looks: LookUsado[];
  prendas: Prenda[];
  onUpdate: (id: number, updates: Partial<LookUsado>) => void;
  loading: boolean;
}

const ESTADOS = [
  { value: 'usado', label: 'Usado', color: 'bg-brand-black text-white' },
  { value: 'planeado', label: 'Planificado', color: 'bg-brand-sage/20 text-brand-black' },
];

type TabView = 'looks' | 'analisis';

export default function Historial({ looks, prendas, onUpdate, loading }: HistorialProps) {
  const [ocasionFilter, setOcasionFilter] = useState('todas');
  const [estadoFilter, setEstadoFilter] = useState('usado');
  const [previewLook, setPreviewLook] = useState<LookUsado | null>(null);
  const [editingLook, setEditingLook] = useState<LookUsado | null>(null);
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TabView>('looks');
  const [analysisPeriod, setAnalysisPeriod] = useState<'semana' | 'mes'>('mes');
  const { upload } = useLookUpload();

  const [localRatings, setLocalRatings] = useState<Record<number, number>>({});
  const [localComments, setLocalComments] = useState<Record<number, string>>({});
  const [savedToast, setSavedToast] = useState<{id: number; field: string} | null>(null);

  const ocasionesUnicas = useMemo(() => {
    const set = new Set<string>();
    looks.forEach((l) => {
      if (l.ocasion) set.add(l.ocasion);
    });
    return Array.from(set).sort();
  }, [looks]);

  const filtered = useMemo(() => {
    return looks
      .filter((l) => {
        const matchOcasion = ocasionFilter === 'todas' || l.ocasion === ocasionFilter;
        const matchEstado = estadoFilter === 'todas' || l.estado === estadoFilter;
        return matchOcasion && matchEstado;
      })
      .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  }, [looks, ocasionFilter, estadoFilter]);

  const handleFileUpload = async (lookId: number, file: File) => {
    setUploadingId(lookId);
    const url = await upload(lookId, file);
    if (url) {
      onUpdate(lookId, { foto_real_url: url });
    }
    setUploadingId(null);
  };

  const handleRate = (lookId: number, rating: number) => {
    setLocalRatings((prev) => ({ ...prev, [lookId]: rating }));
    onUpdate(lookId, { calificacion: rating });
    setSavedToast({ id: lookId, field: 'rating' });
    setTimeout(() => setSavedToast((t) => (t?.id === lookId && t?.field === 'rating' ? null : t)), 2000);
  };

  const handleDeleteComment = (lookId: number) => {
    setLocalComments((prev) => {
      const next = { ...prev };
      delete next[lookId];
      return next;
    });
    onUpdate(lookId, { comentario_cliente: null });
    setSavedToast({ id: lookId, field: 'comment' });
    setTimeout(() => setSavedToast((t) => (t?.id === lookId && t?.field === 'comment' ? null : t)), 2000);
    setEditingLook(null);
  };

  // --- Análisis ---
  const analysis = useMemo(() => {
    const now = new Date();
    const periodStart = analysisPeriod === 'semana'
      ? new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      : new Date(now.getFullYear(), now.getMonth(), 1);

    const periodLooks = looks.filter((l) => {
      const d = new Date(l.fecha);
      return d >= periodStart && l.estado === 'usado';
    });

    const totalValorados = periodLooks.filter((l) => l.calificacion != null).length;
    const avgCalificacion = totalValorados > 0
      ? periodLooks.reduce((sum, l) => sum + (l.calificacion ?? 0), 0) / totalValorados
      : 0;

    // Prendas más usadas
    const prendaCounts = new Map<number, number>();
    periodLooks.forEach((l) => {
      l.prendas_ids.forEach((pid) => {
        prendaCounts.set(pid, (prendaCounts.get(pid) ?? 0) + 1);
      });
    });
    const topPrendas = Array.from(prendaCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([pid, count]) => {
        const p = prendas.find((pr) => pr.id === pid);
        return { prenda: p, count };
      })
      .filter((item): item is { prenda: Prenda; count: number } => !!item.prenda);

    // Ocasiones más frecuentes
    const ocasionCounts = new Map<string, number>();
    periodLooks.forEach((l) => {
      if (l.ocasion) {
        ocasionCounts.set(l.ocasion, (ocasionCounts.get(l.ocasion) ?? 0) + 1);
      }
    });
    const topOcasiones = Array.from(ocasionCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    // Fotos vs sin foto
    const conFoto = periodLooks.filter((l) => l.foto_real_url).length;
    const sinFoto = periodLooks.length - conFoto;

    // Distribución de calificaciones
    const calDist: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    periodLooks.forEach((l) => {
      if (l.calificacion && l.calificacion >= 1 && l.calificacion <= 5) {
        calDist[l.calificacion]++;
      }
    });

    return {
      totalLooks: periodLooks.length,
      totalValorados,
      avgCalificacion: Math.round(avgCalificacion * 10) / 10,
      topPrendas,
      topOcasiones,
      conFoto,
      sinFoto,
      calDist,
    };
  }, [looks, prendas, analysisPeriod]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-brand-black text-3xl font-light">Historial de Looks</h1>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">
            Registra y evalúa las combinaciones que ya usaste
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-white border border-brand-gray-200 rounded-xl p-1 w-fit">
        <button
          onClick={() => setActiveTab('looks')}
          className={`px-4 py-2 rounded-lg font-sans text-sm transition-colors whitespace-nowrap ${
            activeTab === 'looks'
              ? 'bg-brand-black text-white'
              : 'text-brand-gray-600 hover:text-brand-black'
          }`}
        >
          <i className="ri-camera-line mr-1.5" />
          Looks
        </button>
        <button
          onClick={() => setActiveTab('analisis')}
          className={`px-4 py-2 rounded-lg font-sans text-sm transition-colors whitespace-nowrap ${
            activeTab === 'analisis'
              ? 'bg-brand-black text-white'
              : 'text-brand-gray-600 hover:text-brand-black'
          }`}
        >
          <i className="ri-bar-chart-grouped-line mr-1.5" />
          Análisis
        </button>
      </div>

      {/* VISTA: LOOKS */}
      {activeTab === 'looks' && (
        <>
          {/* Filtros */}
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={ocasionFilter}
              onChange={(e) => setOcasionFilter(e.target.value)}
              className="bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
            >
              <option value="todas">Todas las ocasiones</option>
              {ocasionesUnicas.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            <select
              value={estadoFilter}
              onChange={(e) => setEstadoFilter(e.target.value)}
              className="bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
            >
              <option value="usado">Solo usados</option>
              <option value="todas">Todos los estados</option>
              {ESTADOS.map((e) => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
          </div>

          {/* Grid de looks */}
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-brand-gray-500 text-sm py-20">
              <i className="ri-loader-4-line animate-spin" />
              <span>Cargando...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-12 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-brand-gray-50 flex items-center justify-center text-brand-gray-400 mb-4">
                <i className="ri-camera-line text-2xl" />
              </div>
              <p className="font-sans text-brand-gray-500 text-sm">
                No hay looks registrados con esos filtros.
              </p>
              <p className="font-sans text-brand-gray-400 text-xs mt-2">
                Sube una foto del look que usaste para empezar tu historial.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((look) => {
                const lookPrendas = look.prendas_ids
                  .map((pid) => prendas.find((p) => p.id === pid))
                  .filter(Boolean) as Prenda[];
                const hasComment = !!(localComments[look.id] || look.comentario_cliente);

                return (
                  <div key={look.id} className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden hover:border-brand-gray-300 transition-colors">
                    {/* Header */}
                    <div className="p-4 pb-2 flex items-start justify-between gap-3">
                      <div>
                        <p className="font-sans text-brand-black text-sm font-medium line-clamp-1">{look.titulo}</p>
                        <p className="font-sans text-brand-gray-500 text-xs mt-0.5">
                          {new Date(look.fecha).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })}
                          {look.ocasion && ` · ${look.ocasion}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {savedToast?.id === look.id && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-sage/20 text-brand-black font-sans text-[10px] font-medium">
                            <i className="ri-check-line" />
                            Guardado
                          </span>
                        )}
                        <span className={`inline-block px-2 py-0.5 rounded-full font-sans text-[10px] uppercase tracking-wider font-medium ${
                          look.estado === 'usado' ? 'bg-brand-black text-white' : 'bg-brand-sage/20 text-brand-black'
                        }`}>
                          {look.estado === 'usado' ? 'Usado' : 'Planificado'}
                        </span>
                      </div>
                    </div>

                    {/* Foto real o collage */}
                    <div className="relative aspect-[4/3] bg-brand-gray-50">
                      {look.foto_real_url ? (
                        <img src={look.foto_real_url} alt={look.titulo} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center p-4">
                          <div className="grid grid-cols-3 gap-2 w-full h-full">
                            {lookPrendas.slice(0, 6).map((prenda) => (
                              <div key={prenda.id} className="relative rounded-lg overflow-hidden bg-white border border-brand-gray-100">
                                {prenda.imagen_url ? (
                                  <img src={prenda.imagen_url} alt={prenda.nombre} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-brand-gray-300">
                                    <i className="ri-t-shirt-line text-lg" />
                                  </div>
                                )}
                              </div>
                            ))}
                            {lookPrendas.length === 0 && (
                              <div className="col-span-3 flex items-center justify-center text-brand-gray-300">
                                <i className="ri-image-line text-3xl" />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="absolute bottom-3 right-3 flex gap-2">
                        {uploadingId === look.id ? (
                          <span className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-sans text-xs font-medium bg-brand-gray-100 text-brand-gray-500">
                            <i className="ri-loader-4-line animate-spin" />
                            Subiendo...
                          </span>
                        ) : (
                          <>
                            <label className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-sans text-xs font-medium cursor-pointer transition-colors bg-white/90 backdrop-blur-sm text-brand-black hover:bg-white">
                              <i className="ri-camera-fill" />
                              Cámara
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleFileUpload(look.id, file);
                                  e.currentTarget.value = '';
                                }}
                              />
                            </label>
                            <label className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-sans text-xs font-medium cursor-pointer transition-colors bg-white/90 backdrop-blur-sm text-brand-black hover:bg-white">
                              <i className="ri-image-line" />
                              Galería
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleFileUpload(look.id, file);
                                  e.currentTarget.value = '';
                                }}
                              />
                            </label>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-4 space-y-3">
                      <div className="flex flex-wrap gap-1.5">
                        {lookPrendas.map((prenda) => (
                          <span key={prenda.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-brand-gray-50 font-sans text-[11px] text-brand-gray-600">
                            {prenda.imagen_url ? (
                              <img src={prenda.imagen_url} alt="" className="w-4 h-4 rounded object-cover" />
                            ) : (
                              <i className="ri-t-shirt-line text-brand-gray-400" />
                            )}
                            {prenda.nombre}
                          </span>
                        ))}
                      </div>

                      {look.notas && (
                        <p className="font-sans text-brand-gray-400 text-xs bg-brand-cream rounded-lg p-2.5">{look.notas}</p>
                      )}
                      {hasComment && (
                        <div className="bg-brand-gray-50 rounded-lg p-2.5">
                          <p className="font-sans text-brand-gray-500 text-[10px] uppercase tracking-wider font-medium mb-1">Tu comentario</p>
                          <p className="font-sans text-brand-black text-xs">{localComments[look.id] || look.comentario_cliente}</p>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <span className="font-sans text-brand-gray-500 text-xs mr-1">Valoración:</span>
                          {[1, 2, 3, 4, 5].map((star) => {
                            const displayRating = localRatings[look.id] ?? look.calificacion ?? 0;
                            return (
                              <button
                                key={star}
                                onClick={() => handleRate(look.id, star)}
                                className="w-5 h-5 flex items-center justify-center text-amber-400 hover:scale-110 transition-transform"
                              >
                                <i className={star <= displayRating ? 'ri-star-fill' : 'ri-star-line'} />
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setPreviewLook(look)}
                            className="w-7 h-7 rounded-lg bg-brand-gray-50 flex items-center justify-center text-brand-gray-500 hover:text-brand-black hover:bg-brand-gray-100 transition-colors"
                            title="Vista previa"
                          >
                            <i className="ri-eye-line text-xs" />
                          </button>
                          <button
                            onClick={() => setEditingLook(look)}
                            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                              hasComment
                                ? 'bg-brand-black text-white hover:bg-brand-gray-600'
                                : 'bg-brand-gray-50 text-brand-gray-500 hover:text-brand-black hover:bg-brand-gray-100'
                            }`}
                            title={hasComment ? 'Editar comentario' : 'Añadir comentario'}
                          >
                            <i className={hasComment ? 'ri-chat-1-fill text-xs' : 'ri-chat-1-line text-xs'} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* VISTA: ANÁLISIS */}
      {activeTab === 'analisis' && (
        <div className="space-y-6">
          {/* Selector de periodo */}
          <div className="flex items-center gap-2 bg-white border border-brand-gray-200 rounded-xl p-1 w-fit">
            <button
              onClick={() => setAnalysisPeriod('semana')}
              className={`px-4 py-2 rounded-lg font-sans text-sm transition-colors whitespace-nowrap ${
                analysisPeriod === 'semana'
                  ? 'bg-brand-black text-white'
                  : 'text-brand-gray-600 hover:text-brand-black'
              }`}
            >
              Última semana
            </button>
            <button
              onClick={() => setAnalysisPeriod('mes')}
              className={`px-4 py-2 rounded-lg font-sans text-sm transition-colors whitespace-nowrap ${
                analysisPeriod === 'mes'
                  ? 'bg-brand-black text-white'
                  : 'text-brand-gray-600 hover:text-brand-black'
              }`}
            >
              Este mes
            </button>
          </div>

          {/* Stats cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
              <div className="w-8 h-8 flex items-center justify-center text-brand-sage mb-3">
                <i className="ri-calendar-check-line text-xl" />
              </div>
              <p className="font-serif text-brand-black text-2xl font-light">{analysis.totalLooks}</p>
              <p className="font-sans text-brand-gray-500 text-xs mt-1">Looks usados</p>
            </div>
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
              <div className="w-8 h-8 flex items-center justify-center text-amber-500 mb-3">
                <i className="ri-star-line text-xl" />
              </div>
              <p className="font-serif text-brand-black text-2xl font-light">{analysis.avgCalificacion || '-'}</p>
              <p className="font-sans text-brand-gray-500 text-xs mt-1">Valoración media</p>
            </div>
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
              <div className="w-8 h-8 flex items-center justify-center text-brand-black mb-3">
                <i className="ri-camera-line text-xl" />
              </div>
              <p className="font-serif text-brand-black text-2xl font-light">{analysis.conFoto}</p>
              <p className="font-sans text-brand-gray-500 text-xs mt-1">Con foto real</p>
            </div>
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
              <div className="w-8 h-8 flex items-center justify-center text-brand-gray-400 mb-3">
                <i className="ri-image-line text-xl" />
              </div>
              <p className="font-serif text-brand-black text-2xl font-light">{analysis.sinFoto}</p>
              <p className="font-sans text-brand-gray-500 text-xs mt-1">Sin foto real</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Prendas más usadas */}
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
              <h3 className="font-sans text-brand-gray-500 text-[11px] tracking-wider uppercase font-medium mb-4">
                Prendas más usadas
              </h3>
              {analysis.topPrendas.length === 0 ? (
                <p className="font-sans text-brand-gray-400 text-sm text-center py-8">
                  No hay datos suficientes para este periodo.
                </p>
              ) : (
                <div className="space-y-3">
                  {analysis.topPrendas.map(({ prenda, count }, idx) => (
                    <div key={prenda.id} className="flex items-center gap-3">
                      <span className="w-5 font-serif text-brand-gray-400 text-sm">{idx + 1}</span>
                      {prenda.imagen_url ? (
                        <img src={prenda.imagen_url} alt={prenda.nombre} className="w-10 h-12 rounded object-cover" />
                      ) : (
                        <div className="w-10 h-12 rounded bg-brand-gray-50 flex items-center justify-center">
                          <i className="ri-t-shirt-line text-brand-gray-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-brand-black text-sm font-medium truncate">{prenda.nombre}</p>
                        <p className="font-sans text-brand-gray-500 text-xs">{prenda.categoria} · {prenda.color}</p>
                      </div>
                      <div className="flex items-center gap-1 text-brand-black">
                        <span className="font-sans text-sm font-medium">{count}</span>
                        <span className="font-sans text-brand-gray-400 text-xs">usos</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Distribución */}
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
              <h3 className="font-sans text-brand-gray-500 text-[11px] tracking-wider uppercase font-medium mb-4">
                Distribución de valoraciones
              </h3>
              {analysis.totalValorados === 0 ? (
                <p className="font-sans text-brand-gray-400 text-sm text-center py-8">
                  Aún no has valorado ningún look en este periodo.
                </p>
              ) : (
                <div className="space-y-3">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = analysis.calDist[star];
                    const pct = analysis.totalValorados > 0 ? Math.round((count / analysis.totalValorados) * 100) : 0;
                    return (
                      <div key={star} className="flex items-center gap-3">
                        <div className="w-8 flex items-center gap-0.5 text-amber-400">
                          <span className="font-sans text-sm">{star}</span>
                          <i className="ri-star-fill text-xs" />
                        </div>
                        <div className="flex-1 h-2 bg-brand-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-brand-black rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="w-8 font-sans text-brand-gray-500 text-xs text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Ocasiones más frecuentes */}
              {analysis.topOcasiones.length > 0 && (
                <>
                  <h3 className="font-sans text-brand-gray-500 text-[11px] tracking-wider uppercase font-medium mt-6 mb-3">
                    Ocasiones más frecuentes
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {analysis.topOcasiones.map(([ocasion, count]) => (
                      <span key={ocasion} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-cream font-sans text-sm text-brand-black">
                        {ocasion}
                        <span className="text-brand-gray-500 text-xs">{count}x</span>
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal vista previa */}
      {previewLook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setPreviewLook(null)} />
          <div className="relative bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-brand-black text-xl">{previewLook.titulo}</h3>
              <button
                onClick={() => setPreviewLook(null)}
                className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <p className="font-sans text-brand-gray-500 text-sm">
              {new Date(previewLook.fecha).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              {previewLook.ocasion && ` · ${previewLook.ocasion}`}
            </p>

            <div className="grid grid-cols-2 gap-3">
              {previewLook.prendas_ids.map((pid, idx) => {
                const prenda = prendas.find((p) => p.id === pid);
                if (!prenda) return null;
                const isHero = idx === 0;
                return (
                  <div
                    key={pid}
                    className={`relative rounded-xl overflow-hidden bg-brand-gray-50 ${isHero ? 'col-span-2 aspect-[16/9]' : 'aspect-[3/4]'}`}
                  >
                    {prenda.imagen_url ? (
                      <img src={prenda.imagen_url} alt={prenda.nombre} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-brand-gray-300">
                        <i className="ri-t-shirt-line text-3xl" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <p className="font-sans text-white text-sm font-medium">{prenda.nombre}</p>
                      <p className="font-sans text-white/70 text-xs">{prenda.categoria} · {prenda.color}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {previewLook.foto_real_url && (
              <div className="space-y-2">
                <p className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Foto real del look</p>
                <img src={previewLook.foto_real_url} alt="Foto real" className="w-full rounded-xl object-cover" />
              </div>
            )}

            {previewLook.notas && (
              <div className="bg-brand-cream rounded-xl p-4">
                <p className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider font-medium mb-1">Notas</p>
                <p className="font-sans text-brand-black text-sm">{previewLook.notas}</p>
              </div>
            )}

            {previewLook.calificacion && (
              <div className="flex items-center gap-1 text-amber-400">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i key={s} className={s <= previewLook.calificacion! ? 'ri-star-fill' : 'ri-star-line'} />
                ))}
                <span className="font-sans text-brand-gray-500 text-xs ml-2">{previewLook.calificacion}/5</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal comentario */}
      {editingLook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditingLook(null)} />
          <div className="relative bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-brand-black text-lg">
                {editingLook.comentario_cliente ? 'Editar comentario' : 'Añadir comentario'}
              </h3>
              <button
                onClick={() => setEditingLook(null)}
                className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <p className="font-sans text-brand-gray-500 text-sm">{editingLook.titulo}</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.currentTarget);
                const comentario = (fd.get('comentario') as string)?.trim() || null;
                if (comentario) {
                  setLocalComments((prev) => ({ ...prev, [editingLook.id]: comentario }));
                } else {
                  setLocalComments((prev) => {
                    const next = { ...prev };
                    delete next[editingLook.id];
                    return next;
                  });
                }
                onUpdate(editingLook.id, { comentario_cliente: comentario });
                setSavedToast({ id: editingLook.id, field: 'comment' });
                setTimeout(() => setSavedToast((t) => (t?.id === editingLook.id && t?.field === 'comment' ? null : t)), 2000);
                setEditingLook(null);
              }}
              className="space-y-4"
            >
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">¿Cómo te sentiste con este look?</label>
                <textarea
                  name="comentario"
                  defaultValue={editingLook.comentario_cliente ?? ''}
                  rows={4}
                  placeholder="Escribe tu experiencia: ¿funcionó bien? ¿cambiarías algo?"
                  className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
                />
              </div>
              <div className="flex items-center gap-3">
                <button type="submit" className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap">
                  Guardar
                </button>
                <button type="button" onClick={() => setEditingLook(null)} className="border border-brand-gray-200 text-brand-black font-sans text-sm px-5 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap">
                  Cancelar
                </button>
                {editingLook.comentario_cliente && (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('¿Borrar este comentario?')) {
                        handleDeleteComment(editingLook.id);
                      }
                    }}
                    className="ml-auto text-red-500 hover:text-red-700 font-sans text-sm px-3 py-2.5 transition-colors whitespace-nowrap inline-flex items-center gap-1"
                  >
                    <i className="ri-delete-bin-line" />
                    Borrar
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}